import { Inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { AuthenticationService } from './authentication.service';
import { PermissionRoutingService } from '../../shared/permission-routing.service';
import { Constants } from '../../models/constants';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationGuardService {
    constructor(
        private readonly router: Router,
        private readonly authenticationService: AuthenticationService,
        private readonly permissionRoutingService: PermissionRoutingService,
        @Inject(DOCUMENT)
        private readonly document: any,
        @Inject(APP_BASE_HREF)
        private readonly baseHref: string
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.canActivateAsync(route, state);
    }

    async canActivateAsync(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        // check if authentication exists in memory.
        let currentUser = this.authenticationService.getCurrentUser();
        // check if token exists in browser storage.
        if (!currentUser && !(await this.authenticationService.loginWithTokens())) {
            return this.router.parseUrl('/login');
        }

        // get user again and check permission
        currentUser = this.authenticationService.getCurrentUser();
        if (!currentUser) {
            // Didn't expect not being able to get user. throw error
            console.error('Failed to get currentUser after getting token successfully.');
            this.document.location.href = '/error'; // Try to point to a generic error page (not specific to this app)
            return false;
        }

        // normalise the url
        let url = this.baseHref;
        if (this.baseHref.endsWith('/') && state.url.startsWith('/')) {
            url += state.url.substring(1);
        } else if (!this.baseHref.endsWith('/') && !state.url.startsWith('/')) {
            url += '/' + state.url;
        } else {
            url += state.url;
        }

        if (this.permissionRoutingService.canAccessRoute(currentUser, url)) {
            return true;
        }

        const nextRoute = this.permissionRoutingService.getNextAvailableRoute(currentUser, url);
        if (nextRoute) {
            this.document.location.href = nextRoute;
            return false;
        }

        this.authenticationService.configureOAuthServiceLogoutUrlWithMessage(Constants.UserSettingError);
        this.authenticationService.doLogout();
        return false;
    }
}
